import { EXTERNAL_LINKS } from '@psyomics/ui-common';
import { ReactNode } from 'react';
import { Control, Controller, FieldPath } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Link } from '../../../atoms/link/link';
import { Text } from '../../../atoms/text/text';
import { CheckBox } from '../../../molecules/check-box/check-box';

import { Inputs } from './types';

const ControlledCheckbox = ({
  control,
  label,
  name,
  rules,
  variant,
}: {
  readonly control: Control<Inputs>;
  readonly label?: ReactNode;
  readonly name: FieldPath<
    Pick<
      Inputs,
      | 'privacyPolicyAndTermsOfUseAccepted'
      | 'notInCrisis'
      | 'sharingReportForSafety'
      | 'answersToCareTeamAfterQuestionnaire'
    >
  >;
  readonly rules?: object;
  readonly variant?: 'default' | 'error';
}) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { onChange, onBlur, value, ref } }) => (
      <CheckBox
        checked={value}
        id={name}
        label={label}
        onBlur={onBlur}
        onCheckedChange={onChange}
        ref={ref}
        variant={variant}
      />
    )}
    rules={rules}
  />
);

const TermsAndPrivacyCheckbox = ({
  control,
  errors,
}: {
  readonly control: Control<Inputs>;
  readonly errors: Partial<Record<keyof Inputs, { message?: string }>>;
}) => {
  const { t } = useTranslation();
  return (
    <ControlledCheckbox
      control={control}
      label={
        <Text size="sm">
          <Trans
            components={{
              termsLink: (
                <Link
                  className="underline text-blue-600 hover:text-blue-800"
                  href={EXTERNAL_LINKS.TERMS_OF_USE}
                  rel="noopener noreferrer"
                  target="_blank"
                />
              ),
              privacyLink: (
                <Link
                  className="underline text-blue-600 hover:text-blue-800"
                  href={EXTERNAL_LINKS.PRIVACY_POLICY}
                  rel="noopener noreferrer"
                  target="_blank"
                />
              ),
            }}
            i18nKey="auth.register.termsAndPrivacy.agreeText"
            values={{
              termsOfUse: t('auth.register.termsAndPrivacy.termsOfUse'),
              privacyPolicy: t('auth.register.termsAndPrivacy.privacyPolicy'),
            }}
          />
        </Text>
      }
      name="privacyPolicyAndTermsOfUseAccepted"
      rules={{ required: t('auth.register.errors.confirmTAndC') }}
      variant={errors.privacyPolicyAndTermsOfUseAccepted ? 'error' : 'default'}
    />
  );
};

const Checkboxes = ({
  control,
  onUrgentHelpClick,
  errors,
}: {
  readonly control: Control<Inputs>;
  readonly onUrgentHelpClick: () => void;
  readonly errors: Partial<Record<keyof Inputs, { message?: string }>>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <ControlledCheckbox
        control={control}
        label={
          <Text size="sm">
            <Trans
              components={{
                urgentHelpLink: (
                  <button
                    className="text-blue-600 underline cursor-pointer"
                    onClick={onUrgentHelpClick}
                    type="button"
                  ></button>
                ),
              }}
              i18nKey="auth.register.notInCrisisText"
              values={{
                urgentHelp: t('auth.register.notInCrisisUrgentHelpText'),
              }}
            />
          </Text>
        }
        name="notInCrisis"
        rules={{ required: t('auth.register.errors.confirmNotInCrisis') }}
        variant={errors.notInCrisis ? 'error' : 'default'}
      />
      <ControlledCheckbox
        control={control}
        label={t('auth.register.surveyUnderstandingText')}
        name="answersToCareTeamAfterQuestionnaire"
        rules={{ required: t('auth.register.errors.confirmSurveyUnderstanding') }}
        variant={errors.answersToCareTeamAfterQuestionnaire ? 'error' : 'default'}
      />
      <ControlledCheckbox
        control={control}
        label={t('auth.register.sharingInformation')}
        name="sharingReportForSafety"
        rules={{ required: t('auth.register.errors.confirmSharingInformation') }}
        variant={errors.sharingReportForSafety ? 'error' : 'default'}
      />
      <TermsAndPrivacyCheckbox control={control} errors={errors} />
    </>
  );
};

export { Checkboxes };
